// @givmo/org-console — PENDING INVITES: who has been offered administration, and what they get.
//
// The roster answers "who can act for this organization". This answers the question one step before
// it — who has been offered that and has not taken it up — and it is the surface that makes the
// offer in the first place.
//
// ⚠ THE LIST IS LIVE INVITES ONLY, AND THE SERVER DECIDES THAT. It filters both spent and expired
// rows, so everything rendered here is still redeemable and every row's control still does
// something. A panel that also listed dead offers would put a rescind button beside things nobody
// can take.
//
// ⚠ NOTHING HERE MOVES ANYBODY'S ACCESS. An invite is an offer: the roster, the caller's own
// standing and the org-wide switches are untouched by making one or rescinding one, and they stay
// untouched until the invitee redeems — in their own session, which no cache here can see. That is
// why the write invalidations are one key (`org-access-queries.ts`) and why the host's
// `onAccessWritten` seam is deliberately not fired.
//
// ⚠ THE WHOLE PANEL IS GLOBAL-ADMIN-ONLY, ON THE STRICT FLAG — the opposite of the roster's
// fail-open actions column. The roster fails open because its read is ungated and the WRITE is
// server-gated anyway, so a permissive client gate costs nothing; every operation here refuses a
// Member, so rendering it on the permissive flag would show a section whose every request 404s while
// a standing read is merely in flight.
//
// ══ ONE COMPONENT, TWO VIEWS (the request table's shape) ══════════════════════════════════════
//
// Mounted without `module`, it is the organization's own Team access page's invite section: every
// live offer, the org-wide composer. Mounted with `module=<slug>`, it is that module's own Team
// page's invite section — the MODULE-ISSUED surface D-CPO-24 clause 7 names: the list read asks
// the server for that module's offers (`?module=`, and the client narrows the answer again by
// `inviteCarriesModule` — an offered grant or the issuing module — belt and braces), and "Invite
// someone" opens the composer in module-issued mode (that one app, a capped level, never Global
// Admin, `issued_module` on the wire). The heading stays "Invites" in both views (the module is the
// page's own chrome; the description beneath names it), and the per-row offer summary stays
// org-wide (an invite that includes the module still names every app it grants — hiding the rest
// would misstate what the person was offered).
//
// Mounted without `module` on a Global Admin's own Team page inside an app (OM-96), it is Team
// access's section IN PLACE: every live offer, plus one column with each invite's level in THAT app
// (`appColumn`, the team list's own column), and "Invite someone" opens the org-wide composer with
// the app already switched on (`preselectModule`). Every list here names only the apps people can
// open in this console (OM-95, `isServedModule`): an invite's summary leaves the others out, as the
// grid does.
//
// ══ THE MODULE VIEW'S GATE READS A STRING BESIDE THE FLAG ═════════════════════════════════════
//
// The org-wide view is a confirmed Global Admin's. The module view admits a confirmed Global Admin
// OR a holder of the module's `<module>:invite_admins` string in their effective set — the same
// standing read the request panel reads `<module>:approve_access_requests` off — and the list read
// follows that admission (the server's "same authority as creating one"). Which catalogs carry the
// string is the backend's data: a module whose Owner catalog lacks it shows its non-Global-Admin
// Owners no section here, which is that rule rendered, not a defect to paper over.
//
// ══ ROW BY ROW, THE DOOR'S OWN RULES — NEVER A CONTROL THE SERVER REFUSES ═════════════════════
//
// A confirmed Global Admin composes and rescinds anything. A caller admitted by the STRING composes
// only when their own row in the app is Owner (`validate_issuer_scope` refuses a Member-tier issuer
// outright — clause 7's door is "that module's Owner"), and rescinds only an invite that was
// MODULE-ISSUED for this module: an org-issued offer, even one that includes the app, is a Global
// Admin's to rescind, and the server answers 403. Such a row is still listed (it is the app's
// business), with one plain line saying whose it is and no control. A rescind the server refuses
// anyway — a row drawn before the caller's standing changed — is said in words inside the confirm,
// never swallowed.
//
// DATA CONTRACT: the `<prefix>-invites-*` test-id family, the SAME family in both views (the two
// pages never share a DOM). Treat a rename like an API change.

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { MailPlus } from 'lucide-react';
import { isServedModule, testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable, isOrgInviteForbidden } from './org-console-transport';
import { inviteCarriesModule, inviteExpiresIn, inviteOfferSummary } from './org-decode';
import { OrgConsoleUnavailableNotice, OrgTierUnknownNotice } from './org-console-notices';
import { ExternalNote } from './org-external';
import { TEAM_COPY } from './org-team-copy';
import { OrgInviteComposerDialog } from './org-invite-composer-dialog';
import { issuerTierIn } from './org-invite-model';
import { OrgInviteRevokeDialog } from './org-invite-revoke-dialog';
import { GLOBAL_ADMIN_LABEL, ROLE_OWNER, moduleTierLabel, orgLevelWord } from './org-tiers';
import { inviteAdminsPermission } from './standing-read';
import { useOrgAccessGate, useOrgInvites, useRevokeOrgInvite } from './use-org-console';
import type { OrgInviteRow } from './wire-types';

export interface OrgInvitesPanelProps {
  config: OrgConsoleConfig;
  /**
   * The module view: only the invites that carry ONE module (the server scopes the read; the client
   * narrows the answer again by an offered grant or the issuing module), and "Invite someone" opens
   * the MODULE-ISSUED composer for it. Absent ⇒ the org-wide view.
   */
  module?: string | null;
  /**
   * The host page already carries a heading of its own, so render this section's for assistive
   * technology alone — the roster panel's `headingHidden` rule, for the same reason.
   */
  headingHidden?: boolean;
  /**
   * The ORG-WIDE view opened inside an app (OM-96): a Global Admin's app Team page carries Team
   * access's invites, and "Invite someone" opens the whole invite with this app already on. Only
   * read without `module`.
   */
  preselectModule?: string;
  /**
   * The org-wide view inside an app (OM-96): one more column, each invite's level in THIS app, the
   * team list's own column, so "As: Member" is never the only word for what someone will get there.
   * Only read without `module`.
   */
  appColumn?: string | null;
}

export function OrgInvitesPanel({
  config,
  module = null,
  headingHidden = false,
  preselectModule,
  appColumn,
}: OrgInvitesPanelProps) {
  const prefix = `${testIdPrefix(config)}-invites`;
  const gate = useOrgAccessGate(config.orgRef);
  const self = gate.self;
  const filtered = (module ?? '').trim();
  const inModule = filtered.length > 0;
  const inAppColumn = inModule ? '' : (appColumn ?? '').trim();
  const moduleLabel = inModule ? config.vocab.moduleLabel(filtered) : '';
  // ── The gate — see the header ──────────────────────────────────────────────────────────────
  // The org-wide view: the STRICT flag. The module view: that OR the module's invite string in the
  // caller's SETTLED effective set. `allowed` gates the fetch itself, so nobody spends a request
  // the backend refuses while a standing read is merely in flight.
  const holdsInviteString =
    inModule && (self?.permissions ?? []).includes(inviteAdminsPermission(filtered));
  const globalAdminConfirmed = gate.isGlobalAdminConfirmed;
  const allowed = globalAdminConfirmed || holdsInviteString;
  // Whether the section renders at all: fail toward showing while the standing read is in flight
  // or errored (the server is the backstop); nothing at all for a RESOLVED non-holder.
  const admitted = inModule ? !(self !== null && !allowed) : gate.isGlobalAdmin;
  const list = useOrgInvites(config.orgRef, allowed, inModule ? filtered : null);
  const revoke = useRevokeOrgInvite(config.orgRef);

  // ── Row by row — see the header ────────────────────────────────────────────────────────────
  const issuerIsOwner = inModule && issuerTierIn(self, filtered) === ROLE_OWNER;
  const mayCompose = inModule ? globalAdminConfirmed || (holdsInviteString && issuerIsOwner) : true;
  const mayRescind = (invite: OrgInviteRow): boolean =>
    !inModule || globalAdminConfirmed || (holdsInviteString && invite.issued_module === filtered);
  // The rescind's refusal, in words. The door's 403 in the module view is the one sentence this
  // surface says better than the server; every other refusal is the server's own text, verbatim —
  // the composer's rule, for the composer's reason.
  const rescindRefusal = (error: unknown): string =>
    inModule && isOrgInviteForbidden(error)
      ? `You can only rescind invites issued from ${moduleLabel}.`
      : error instanceof Error && error.message.length > 0
        ? error.message
        : 'Something went wrong. Please try again.';

  const [composing, setComposing] = useState(false);
  const [revoking, setRevoking] = useState<OrgInviteRow | null>(null);
  // A fresh confirm never wears the last one's refusal.
  const beginRevoke = (invite: OrgInviteRow) => {
    revoke.reset();
    setRevoking(invite);
  };

  const invites = useMemo(() => {
    const all = list.data ?? [];
    if (!inModule) return all;
    return all.filter((invite) => inviteCarriesModule(invite, filtered));
  }, [list.data, inModule, filtered]);
  const absent =
    (list.isError && isOrgConsoleUnavailable(list.error)) ||
    (gate.isUnavailable && !list.isSuccess);

  if (absent) {
    return (
      <OrgConsoleUnavailableNotice
        testId={`${prefix}-unavailable`}
        title="Invites"
        description={`This environment doesn’t have the console turned on. Nothing is wrong with your account — there’s just nothing here to open yet.`}
      />
    );
  }

  // ⚠ A RESOLVED NON-HOLDER SEES NOTHING AT ALL, not a disabled section. Every operation here
  // refuses them, and a section of controls that all fail is worse than a section that was never
  // claimed to exist. While the standing read is still in flight or has errored, the section renders
  // — the same fail-toward-showing direction the rest of this package takes, and the server is the
  // backstop.
  if (!admitted) return null;

  // ⚠ THE TABLE IS LOADING UNTIL A READ HAS ACTUALLY HAPPENED, and the standing read counts. The list
  // read is gated on the settled admission, so while the standing is in flight the list query is
  // disabled — and a disabled query is not "loading" — which rendered "No invites outstanding" for
  // an organization whose invites had not been asked for yet. An empty state is a claim; this
  // surface may only make it about a list it read.
  const loading = gate.isPending || (allowed && list.isPending);
  // ⚠ AND WHEN THE STANDING READ FAILED THERE IS NOTHING TO SAY ABOUT THE LIST AT ALL — the read
  // never fired. That is the recoverable state this package already has a sentence for, and it is
  // pointedly not "you may not be here": collapsing them asserts something the surface never
  // learned. The composer above it stays, because the catalog read it needs is ungated and a Global
  // Admin should not lose the act over one flaky request.
  const listError =
    list.isError && !isOrgConsoleUnavailable(list.error)
      ? 'We couldn’t load your invites.'
      : undefined;

  const columns: DataTableColumn<OrgInviteRow>[] = [
    {
      id: 'email',
      header: 'Invited',
      width: '45%',
      cell: (invite) => (
        <div className="min-w-0">
          {/* The roster's External note, on the address's line: the invitee is marked when they
              accept (§OM-53). */}
          <div className="flex min-w-0 items-baseline gap-8">
            <div className="truncate font-w600 text-text-strong">
              {invite.email.length > 0 ? invite.email : 'An address we can no longer read'}
            </div>
            {invite.external && <ExternalNote testId={`${prefix}-row-${invite.id}-external`} />}
          </div>
          {/* Expiry belongs under the address rather than in a column of its own: it is the one
              fact that decides whether this row still matters, and at phone width a fourth column
              is a fourth thing to fit before the reader gets to the action. */}
          {inviteExpiresIn(invite).length > 0 && (
            <div
              className="truncate text-s13 text-text-tertiary"
              {...testIdProp(`${prefix}-row-${invite.id}-expiry`)}
            >
              {inviteExpiresIn(invite)}
            </div>
          )}
          {/* Whose it is, on a row this caller may not rescind — the line that explains the missing
              control, in both readings (the actions column is omitted from the stacked one). */}
          {!mayRescind(invite) && (
            <div
              className="truncate text-s13 text-text-tertiary"
              {...testIdProp(`${prefix}-row-${invite.id}-org-issued`)}
            >
              A {GLOBAL_ADMIN_LABEL}’s invite
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'role',
      header: 'As',
      // ⚠ "GLOBAL ADMIN" OR NOTHING (OM-96, D-CPO-77 item 7): the org-level word only where it says
      // something, as the team list's Access column. What anyone else will get is per app — the
      // app's own column in an app's frame, the Apps summary beside it.
      cell: (invite) => {
        const word = orgLevelWord(invite.role);
        return (
          <span {...testIdProp(`${prefix}-row-${invite.id}-role`)}>
            {word !== null && <Badge tone="brand">{word}</Badge>}
          </span>
        );
      },
    },
    // In an app's frame (OM-96): the level the invite gives in that app — a Global Admin's offer
    // holds every app at Owner, as the team list says of a Global Admin.
    ...(inAppColumn.length === 0
      ? []
      : [
          {
            id: 'app',
            header: config.vocab.moduleLabel(inAppColumn),
            cell: (invite: OrgInviteRow) => {
              const level =
                invite.role.trim() === ROLE_OWNER
                  ? ROLE_OWNER
                  : invite.module_grants.find((grant) => grant.module === inAppColumn)
                      ?.granted_role;
              return (
                <span {...testIdProp(`${prefix}-row-${invite.id}-app`)}>
                  {level === undefined ? (
                    <span className="text-text-tertiary">{TEAM_COPY.noAccess}</span>
                  ) : (
                    <Badge tone={moduleTierLabel(level) === ROLE_OWNER ? 'brand' : 'neutral'}>
                      {moduleTierLabel(level)}
                    </Badge>
                  )}
                </span>
              );
            },
          } satisfies DataTableColumn<OrgInviteRow>,
        ]),
    {
      id: 'offer',
      header: 'Apps',
      cell: (invite) => (
        <span className="text-text-secondary" {...testIdProp(`${prefix}-row-${invite.id}-offer`)}>
          {/* Only the apps people can open here (OM-95), like every other list. */}
          {inviteOfferSummary(
            {
              ...invite,
              module_grants: invite.module_grants.filter((grant) =>
                isServedModule(config, grant.module),
              ),
            },
            config.vocab.moduleLabel,
          )}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right' as const,
      width: '140px',
      // ⚠ OMITTED FROM THE STACKED READING, and the row's own action replaces it (OM-13). A button
      // inside a block whose action is elsewhere would be two targets in one place.
      stacked: 'omit' as const,
      cell: (invite) =>
        mayRescind(invite) ? (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => beginRevoke(invite)}
            testId={`${prefix}-row-${invite.id}-revoke`}
          >
            Rescind
          </Button>
        ) : null,
    },
  ];

  return (
    <section className="flex flex-col gap-12" {...testIdProp(prefix)}>
      <div className="flex flex-wrap items-start justify-between gap-12">
        <div className="min-w-0">
          <h2
            className={
              headingHidden
                ? 'sr-only'
                : 'font-sans text-s16 font-w700 tracking-givmo text-text-strong'
            }
          >
            Invites
          </h2>
          <p className="mt-4 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
            {inModule
              ? `People who have been offered a place that includes ${moduleLabel} and have not taken it up yet.`
              : `People who have been offered a place on this ${config.vocab.orgNoun}’s team and have not taken it up yet.`}
          </p>
        </div>
        {mayCompose && (
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            onClick={() => setComposing(true)}
            testId={`${prefix}-compose`}
          >
            Invite someone
          </Button>
        )}
      </div>

      {gate.tierUnknown ? (
        <OrgTierUnknownNotice testId={`${prefix}-tier-retry`} onRetry={gate.refetch} />
      ) : (
        <DataTable
          testId={`${prefix}-list`}
          columns={columns}
          rows={invites}
          rowKey={(invite) => invite.id}
          rowTestId={() => `${prefix}-row`}
          // Below `sm` each row is a stacked block and the row itself carries the action — the roster's
          // rule, and the same defect it cures: at 390px a four-column table shows the address and
          // hides everything that decides what to do about it.
          stackAt="sm"
          onStackedRowAction={(invite: OrgInviteRow) => beginRevoke(invite)}
          stackedRowActionWhen={mayRescind}
          stackedRowActionLabel={(invite: OrgInviteRow) => `Rescind the invite for ${invite.email}`}
          // ⚠ ITS OWN ID, never the button's — only one renders at a given width, but a shared id makes
          // `getByTestId` ambiguous in any test that renders both readings.
          stackedRowActionTestId={(invite: OrgInviteRow) => `${prefix}-row-${invite.id}-open`}
          loading={loading}
          loadingLabel="Loading your invites…"
          error={listError}
          onRetry={() => void list.refetch()}
          emptyState={
            <EmptyState
              testId={`${prefix}-empty-state`}
              icon={<Icon icon={MailPlus} size={20} aria-hidden />}
              title="No invites outstanding"
              description={
                inModule
                  ? `Nobody has been offered a place that includes ${moduleLabel} yet.`
                  : `Everyone who has been offered a place on this ${config.vocab.orgNoun}’s team, and not yet taken it, will appear here.`
              }
            />
          }
          // Rows LOADED — never "of N". `meta` is discarded by the unwrap before this component can
          // see it, and no footer renders until a read has actually resolved: a count printed under
          // skeletons or under an error message is a claim this panel never learned.
          footer={
            loading || list.isError ? undefined : (
              <>
                <span
                  className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums"
                  {...testIdProp(`${prefix}-count`)}
                >
                  {invites.length} {invites.length === 1 ? 'invite' : 'invites'}
                </span>
                {list.hasNextPage ? (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    disabled={list.isFetchingNextPage}
                    onClick={() => void list.fetchNextPage()}
                    testId={`${prefix}-load-more`}
                  >
                    {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                  </Button>
                ) : null}
              </>
            )
          }
        />
      )}

      <OrgInviteComposerDialog
        config={config}
        open={composing}
        onOpenChange={(open) => setComposing(open)}
        issuedModule={inModule ? filtered : undefined}
        preselectModule={inModule ? undefined : preselectModule}
      />
      <OrgInviteRevokeDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (open) return;
          setRevoking(null);
          revoke.reset();
        }}
        email={revoking?.email ?? ''}
        testIdPrefix={prefix}
        loading={revoke.isPending}
        error={revoke.isError ? rescindRefusal(revoke.error) : null}
        onConfirm={() => {
          if (revoking === null) return;
          revoke.mutate(
            { inviteId: revoking.id },
            // Closed on success ONLY. A rescind that failed leaves the dialog standing with its
            // subject named AND the refusal said (`error`, above) — closing would let the row
            // reappear on the next read with nothing said about why, and standing in silence reads
            // as the control not working.
            { onSuccess: () => setRevoking(null) },
          );
        }}
      />
    </section>
  );
}
